import { lazy, type Component } from "solid-js";
import { PERMISSIONS } from "@/shared/auth/permissions";
import { can } from "@/shared/auth/session";
import type { PrepEvent } from "./games/prep/brief";
import {
  BarChartIcon,
  BookIcon,
  CalendarIcon,
  CrosswordIcon,
  HeartIcon,
  KeyIcon,
  LinkIcon,
  ListIcon,
  PenIcon,
  PeopleIcon,
  PhoneIcon,
  PuzzleIcon,
  ShieldIcon,
  SquaresIcon,
  TrophyIcon,
  TypeIcon,
  ZapIcon,
} from "@/shared/components/icons";

export type IconComponent = Component<{ size?: number }>;

export type NavItem = {
  path: string;
  label: string;
  icon: IconComponent;
  component: Component;
  /** Match the path exactly instead of as a prefix. Set on an app's index view. */
  end?: boolean;
  /**
   * The permission a user needs before this page is offered to them. It is
   * required rather than optional so that adding a page is a moment where
   * somebody has to decide who may see it.
   */
  permission: string;
  /** The heading this page sits under in the sidebar: one of its app's sections. */
  section?: string;
};

export type AppDef = {
  id: string;
  label: string;
  basePath: string;
  icon: IconComponent;
  nav: NavItem[];
  /**
   * Headings the sidebar groups this app's pages under, in order. A heading is
   * shown even before any page sits under it, so a section can be announced
   * ahead of its pages.
   */
  sections?: string[];
};

const StudySheetView = lazy(() => import("./games/prep/StudySheetView"));
const AcronymDrillView = lazy(() => import("./games/prep/AcronymDrillView"));
const AcronymSurvivalView = lazy(
  () => import("./games/prep/AcronymSurvivalView"),
);

const DAILY_GAMES = "Daily Games";
const HR_TECH_PREP = "Prep Games - HR Tech";

/**
 * One event's prep games, all drilling that event's brief, under its heading.
 * The heading has to be in the app's sections as well, or the pages sit
 * above every heading instead of under their own.
 */
function prepGames(event: PrepEvent, section: string): NavItem[] {
  const base = `/games/prep/${event}`;
  return [
    {
      path: `${base}/study`,
      label: "Study Sheet",
      icon: BookIcon,
      section,
      permission: PERMISSIONS.games.play,
      component: () => <StudySheetView event={event} />,
    },
    {
      path: `${base}/acronym-drill`,
      label: "Acronym Drill",
      icon: ZapIcon,
      section,
      permission: PERMISSIONS.games.play,
      component: () => <AcronymDrillView event={event} />,
    },
    {
      path: `${base}/acronym-survival`,
      label: "Acronym Survival",
      icon: HeartIcon,
      section,
      permission: PERMISSIONS.games.play,
      component: () => <AcronymSurvivalView event={event} />,
    },
  ];
}

/**
 * Every app in the sidebar. Adding one means adding a directory under `apps/`
 * and an entry here -- the shell, the router, and the app switcher all read
 * from this list. Views are lazily imported so each app is its own chunk.
 */
export const apps: AppDef[] = [
  {
    id: "calendar",
    label: "Marketing Calendar",
    basePath: "/calendar",
    icon: CalendarIcon,
    nav: [
      {
        path: "/calendar",
        label: "Calendar",
        icon: CalendarIcon,
        end: true,
        permission: PERMISSIONS.calendar.overview,
        component: lazy(() => import("./calendar/views/CalendarView")),
      },
      {
        // Events rather than the overview: this page reads the events and
        // campaigns endpoints directly rather than the combined one, and a
        // list that could only ever be empty is worse than no entry at all.
        // Campaigns are gated within the page, which is the half that can be
        // missing without emptying it.
        path: "/calendar/list",
        label: "List",
        icon: ListIcon,
        permission: PERMISSIONS.calendar.eventsView,
        component: lazy(() => import("./calendar/views/ListView")),
      },
      {
        path: "/calendar/people",
        label: "People",
        icon: PeopleIcon,
        permission: PERMISSIONS.directory.view,
        component: lazy(() => import("./calendar/views/PeopleView")),
      },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    basePath: "/sales",
    icon: BarChartIcon,
    nav: [
      {
        path: "/sales",
        label: "SDR Dashboard",
        icon: BarChartIcon,
        end: true,
        permission: PERMISSIONS.sales.dashboard,
        component: lazy(() => import("./sales/views/DashboardView")),
      },
      {
        path: "/sales/reps",
        label: "Reps",
        icon: PeopleIcon,
        permission: PERMISSIONS.sales.repsView,
        component: lazy(() => import("./sales/views/RepsView")),
      },
    ],
  },
  {
    id: "games",
    label: "Games",
    basePath: "/games",
    icon: PuzzleIcon,
    // Daily games are one shared pack a day, played once. Prep games can be
    // played over and over, and drill one event's brief; each event gets its
    // own section, so its material stays apart from the next one's.
    sections: [DAILY_GAMES, HR_TECH_PREP],
    nav: [
      {
        path: "/games",
        label: "Crossword",
        icon: CrosswordIcon,
        end: true,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.play,
        component: lazy(() => import("./games/views/CrosswordView")),
      },
      {
        path: "/games/four-of-a-kind",
        label: "Four of a Kind",
        icon: SquaresIcon,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.play,
        component: lazy(() => import("./games/views/FourOfAKindView")),
      },
      {
        path: "/games/jargonle",
        label: "Jargonle",
        icon: TypeIcon,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.play,
        component: lazy(() => import("./games/views/JargonleView")),
      },
      {
        path: "/games/match-up",
        label: "Match-Up",
        icon: LinkIcon,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.play,
        component: lazy(() => import("./games/views/MatchUpView")),
      },
      {
        path: "/games/acronyms",
        label: "Acronym Sprint",
        icon: ZapIcon,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.play,
        component: lazy(() => import("./games/views/AcronymsView")),
      },
      {
        path: "/games/redline",
        label: "Redline",
        icon: PenIcon,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.play,
        component: lazy(() => import("./games/views/RedlineView")),
      },
      {
        path: "/games/leaderboard",
        label: "Leaderboard",
        icon: TrophyIcon,
        section: DAILY_GAMES,
        permission: PERMISSIONS.games.leaderboardView,
        component: lazy(() => import("./games/views/LeaderboardView")),
      },
      ...prepGames("hr-tech", HR_TECH_PREP),
    ],
  },
  {
    id: "receptionist",
    label: "Receptionist",
    basePath: "/receptionist",
    icon: PhoneIcon,
    nav: [
      {
        path: "/receptionist",
        label: "Messages",
        icon: ListIcon,
        end: true,
        permission: PERMISSIONS.receptionist.messagesView,
        component: lazy(() => import("./receptionist/views/MessagesView")),
      },
      {
        path: "/receptionist/knowledge",
        label: "Knowledge",
        icon: BookIcon,
        permission: PERMISSIONS.receptionist.knowledgeView,
        component: lazy(() => import("./receptionist/views/KnowledgeView")),
      },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    basePath: "/admin",
    icon: ShieldIcon,
    nav: [
      {
        path: "/admin/users",
        label: "Users",
        icon: PeopleIcon,
        permission: PERMISSIONS.adminUsers.view,
        component: lazy(() => import("./admin/views/UsersView")),
      },
      {
        path: "/admin/roles",
        label: "Roles",
        icon: KeyIcon,
        permission: PERMISSIONS.adminRoles.view,
        component: lazy(() => import("./admin/views/RolesView")),
      },
    ],
  },
];

export const navItems: NavItem[] = apps.flatMap((app) => app.nav);

/** The pages this user may open, in sidebar order. */
export function allowedNav(app: AppDef): NavItem[] {
  return app.nav.filter((item) => can(item.permission));
}

export type NavGroup = { heading?: string; items: NavItem[] };

/**
 * The pages this user may open, grouped under their app's headings in sidebar
 * order. Pages outside any section come first, without a heading. A heading
 * with no pages yet is kept; one whose pages this user may not open is not.
 */
export function navGroups(app: AppDef): NavGroup[] {
  const sections = app.sections ?? [];
  const allowed = allowedNav(app);
  const groups: NavGroup[] = [];
  const loose = allowed.filter(
    (item) => !item.section || !sections.includes(item.section),
  );
  if (loose.length) groups.push({ items: loose });
  for (const heading of sections) {
    const items = allowed.filter((item) => item.section === heading);
    const announced = !app.nav.some((item) => item.section === heading);
    if (items.length || announced) groups.push({ heading, items });
  }
  return groups;
}

/** The apps with at least one page this user may open. */
export function allowedApps(): AppDef[] {
  return apps.filter((app) => allowedNav(app).length > 0);
}

/**
 * Where to send somebody who has just signed in, or who asked for a page they
 * may not have. Null means their role grants them nothing, which is a state the
 * caller has to say something about rather than redirect out of.
 */
export function landingPath(): string | null {
  for (const app of allowedApps()) {
    const first = allowedNav(app)[0];
    if (first) return first.path;
  }
  return null;
}

export function appForPath(pathname: string): AppDef {
  return apps.find((app) => pathname.startsWith(app.basePath)) ?? apps[0]!;
}
